/** General tab — mirrors the editable fields of `Tour_OperatorAdmin`
 *  scoped to identity / legal / self-reg / UI copy. Billing, payment,
 *  booking behaviour, configuration JSON, and related-FK display moved
 *  out (will land on dedicated tabs).
 *
 *  The code is shown read-only and never sent (owner 2026-10-21): it is the
 *  tenant's dashboard address and MCP URL, fixed once the tenant exists.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as topsApi from '@/api/tourOperators';
import type { TourOperatorDetail, TourOperatorWriteInput } from '@/api/tourOperators';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';

import { FieldActionsAdornment, isHttpUrl } from '@/components/ValueActions/FieldActionsAdornment';
import { codeActions, emailActions, franchiseFlagActions, urlActions } from '../tenantLinks';

// Copy the mobile apps ship for the "Find my booking" screen. Shown as
// placeholders only — an empty field is saved as null and the app falls back to
// its own built-in string. Kept in sync with
// Tour_Operator.FIND_MY_TRIP_TEXT_DEFAULTS on the backend.
const FIND_MY_BOOKING_DEFAULTS = {
  find_my_trip_header_text: 'Please enter your booking number and last name',
  find_my_trip_ref_label: 'Booking Ref.',
  find_my_trip_name_label: 'Last Name',
  find_my_trip_error_text:
    "We can't find a booking matching your details. Please check that your last name and booking number are correct. If your booking is starting soon, contact us via chat for assistance.",
} as const;

// --------------------------------------------------------------------------
// Form schema — only the fields shown on this tab. Optional everywhere
// because PATCH is partial; required-on-model fields keep their value when
// untouched.
// --------------------------------------------------------------------------

const optionalString = z.string().optional().nullable();
const optionalUrl = z.union([z.string().url(), z.literal('')]).optional().nullable();

const FormSchema = z.object({
  name: z.string().min(1, 'Required'),
  is_franchise: z.boolean(),
  email: z.union([z.string().email(), z.literal('')]).optional().nullable(),
  url: optionalUrl,
  bio: optionalString,
  has_dashboard_access: z.boolean(),
  terms_and_conditions_url: optionalUrl,
  privacy_policy_url: optionalUrl,
  app_policy_text: optionalString,
  marketing_consent_text: optionalString,
  selfreg_page_title: optionalString,
  privacy_policy_notice: optionalString,
  post_selfreg_text: optionalString,
  trip_types_text: optionalString,
  search_bar_text: optionalString,
  search_bar_title: optionalString,
  find_my_trip_header_text: optionalString,
  find_my_trip_ref_label: optionalString,
  find_my_trip_name_label: optionalString,
  find_my_trip_error_text: optionalString,
});

type FormValues = z.infer<typeof FormSchema>;

type ValueActionList = ValueAction[];

// --------------------------------------------------------------------------

interface Props {
  to: TourOperatorDetail;
  onSaved: () => void;
}

export function GeneralTab({ to, onSaved }: Props) {
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canEdit = hasPerm('console_top_manage');

  const defaults: FormValues = {
    name: to.name ?? '',
    is_franchise: to.is_franchise,
    email: to.email ?? '',
    url: to.url ?? '',
    bio: to.bio ?? '',
    has_dashboard_access: to.has_dashboard_access,
    terms_and_conditions_url: to.terms_and_conditions_url ?? '',
    privacy_policy_url: to.privacy_policy_url ?? '',
    app_policy_text: to.app_policy_text ?? '',
    marketing_consent_text: to.marketing_consent_text ?? '',
    selfreg_page_title: to.selfreg_page_title ?? '',
    privacy_policy_notice: to.privacy_policy_notice ?? '',
    post_selfreg_text: to.post_selfreg_text ?? '',
    trip_types_text: to.trip_types_text ?? '',
    search_bar_text: to.search_bar_text ?? '',
    search_bar_title: to.search_bar_title ?? '',
    find_my_trip_header_text: to.find_my_trip_header_text ?? '',
    find_my_trip_ref_label: to.find_my_trip_ref_label ?? '',
    find_my_trip_name_label: to.find_my_trip_name_label ?? '',
    find_my_trip_error_text: to.find_my_trip_error_text ?? '',
  };

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: defaults,
  });

  const mutation = useMutation({
    mutationFn: (body: TourOperatorWriteInput) => topsApi.update(to.id, body),
    onSuccess: () => {
      snackbar.show('Tour operator updated.', 'success');
      onSaved();
    },
    onError: (e: unknown) => {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Update failed.', 'error');
    },
  });

  function buildPatch(v: FormValues): TourOperatorWriteInput {
    return {
      name: v.name,
      is_franchise: v.is_franchise,
      email: v.email || null,
      url: v.url || null,
      bio: v.bio || null,
      has_dashboard_access: v.has_dashboard_access,
      terms_and_conditions_url: v.terms_and_conditions_url || null,
      privacy_policy_url: v.privacy_policy_url || null,
      app_policy_text: v.app_policy_text || null,
      marketing_consent_text: v.marketing_consent_text || null,
      selfreg_page_title: v.selfreg_page_title || null,
      privacy_policy_notice: v.privacy_policy_notice || null,
      post_selfreg_text: v.post_selfreg_text || null,
      trip_types_text: v.trip_types_text || null,
      search_bar_text: v.search_bar_text || null,
      search_bar_title: v.search_bar_title || null,
      // Empty clears the override and hands the string back to the app's
      // built-in default -- hence `|| null`, not `?? null`.
      find_my_trip_header_text: v.find_my_trip_header_text || null,
      find_my_trip_ref_label: v.find_my_trip_ref_label || null,
      find_my_trip_name_label: v.find_my_trip_name_label || null,
      find_my_trip_error_text: v.find_my_trip_error_text || null,
    };
  }

  const onSubmit = handleSubmit((v) => mutation.mutate(buildPatch(v)));

  // Adornments are computed from the LIVE value, so a field the operator just
  // edited offers the actions for what it now says, not for what was loaded.
  const [liveEmail, liveUrl, liveTerms, livePrivacy] = useWatch({
    control,
    name: ['email', 'url', 'terms_and_conditions_url', 'privacy_policy_url'],
  });
  const trimmed = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const linkActions = (value: unknown, label: string) =>
    isHttpUrl(trimmed(value)) ? urlActions(trimmed(value), label) : [];

  // Custom save UI (no EditorSaveBar), so the navigation guard is wired here.
  // The TO tabs unmount on switch, so in-record tab changes rightly prompt too.
  const { prompt } = useUnsavedChangesPrompt(isDirty && canEdit && !mutation.isPending, {
    message: 'You have unsaved changes on this tour operator. Leaving now will discard them.',
  });

  const text = (
    name: keyof FormValues,
    label: string,
    opts: {
      multiline?: boolean;
      rows?: number;
      type?: string;
      helper?: string;
      placeholder?: string;
      /** Quick actions for the field's CURRENT value — never a link in place
       *  of the input, so typing and the primary click stay untouched. */
      actions?: ValueActionList;
    } = {},
  ) => (
    <TextField
      {...register(name)}
      label={label}
      fullWidth
      size="small"
      multiline={opts.multiline}
      rows={opts.rows}
      type={opts.type ?? 'text'}
      placeholder={opts.placeholder}
      // Without this the label sits on top of the placeholder instead of
      // floating above it.
      InputLabelProps={opts.placeholder ? { shrink: true } : undefined}
      InputProps={
        opts.actions && opts.actions.length > 0
          ? { endAdornment: <FieldActionsAdornment actions={opts.actions} label={`${label} actions`} /> }
          : undefined
      }
      disabled={!canEdit}
      error={Boolean(errors[name])}
      helperText={(errors[name]?.message as string | undefined) ?? opts.helper}
    />
  );

  const bool = (name: keyof FormValues, label: string, actions?: ValueActionList) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(field.value)}
                onChange={(e) => field.onChange(e.target.checked)}
                disabled={!canEdit}
              />
            }
            label={label}
          />
          {/* The checkbox keeps its whole click (label included) — the actions
              live in an adjacent trigger, and only while the flag is on: an
              unticked "franchise parent" has no licensees to find. */}
          {actions && actions.length > 0 && Boolean(field.value) && (
            <Typography variant="caption" color="text.secondary" component="span">
              <ValueActions actions={actions}>Licensees</ValueActions>
            </Typography>
          )}
        </Box>
      )}
    />
  );

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      {prompt}
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access. The `console_top_manage` permission is required to edit.
        </Alert>
      )}

      <Section title="Identity">
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>{text('name', 'Name')}</Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Code"
              value={to.code ?? ''}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: to.code ? (
                  <FieldActionsAdornment actions={codeActions(to.code)} label="Code actions" />
                ) : undefined,
              }}
              helperText="Permanent: the tenant’s dashboard address and MCP URL. It cannot be changed once the tenant exists."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {text('email', 'Email', {
              actions: trimmed(liveEmail) ? emailActions(trimmed(liveEmail)) : [],
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {text('url', 'Website', { actions: linkActions(liveUrl, 'Open website') })}
          </Grid>
          <Grid item xs={12}>{text('bio', 'Bio', { multiline: true, rows: 3 })}</Grid>
          <Grid item xs={12} md={6}>
            {bool('is_franchise', 'Is franchise parent', franchiseFlagActions({ id: to.id, code: to.code }))}
          </Grid>
          <Grid item xs={12} md={6}>{bool('has_dashboard_access', 'Staff users have dashboard access')}</Grid>
        </Grid>
      </Section>

      <Section title="Legal / consent">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {text('terms_and_conditions_url', 'Terms and conditions URL', {
              actions: linkActions(liveTerms, 'Open terms and conditions'),
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {text('privacy_policy_url', 'Privacy policy URL', {
              actions: linkActions(livePrivacy, 'Open privacy policy'),
            })}
          </Grid>
          <Grid item xs={12}>{text('app_policy_text', 'App policy text', { multiline: true, rows: 3 })}</Grid>
          <Grid item xs={12}>{text('marketing_consent_text', 'Marketing consent text', { multiline: true, rows: 3 })}</Grid>
        </Grid>
      </Section>

      <Section title="Self-registration copy">
        <Grid container spacing={2}>
          <Grid item xs={12}>{text('selfreg_page_title', 'Self-registration page title', { multiline: true, rows: 2 })}</Grid>
          <Grid item xs={12}>{text('privacy_policy_notice', 'Privacy policy notice', { multiline: true, rows: 2 })}</Grid>
          <Grid item xs={12}>{text('post_selfreg_text', 'Post self-registration text', { multiline: true, rows: 2 })}</Grid>
        </Grid>
      </Section>

      <Section title="UI copy">
        <Grid container spacing={2}>
          <Grid item xs={12}>{text('trip_types_text', 'Trip types text')}</Grid>
          <Grid item xs={12} md={6}>{text('search_bar_title', 'Search bar title')}</Grid>
          <Grid item xs={12} md={6}>{text('search_bar_text', 'Search bar text')}</Grid>
        </Grid>
      </Section>

      <Section title="Find my booking">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {text('find_my_trip_header_text', 'Header', {
              placeholder: FIND_MY_BOOKING_DEFAULTS.find_my_trip_header_text,
              helper: 'Leave empty to keep the wording shown as the placeholder.',
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {text('find_my_trip_ref_label', 'Booking reference field label', {
              placeholder: FIND_MY_BOOKING_DEFAULTS.find_my_trip_ref_label,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {text('find_my_trip_name_label', 'Name field label', {
              placeholder: FIND_MY_BOOKING_DEFAULTS.find_my_trip_name_label,
              helper: 'Cosmetic: the lookup accepts a last name or a full name either way.',
            })}
          </Grid>
          <Grid item xs={12}>
            {text('find_my_trip_error_text', '"Booking not found" message', {
              multiline: true,
              rows: 3,
              placeholder: FIND_MY_BOOKING_DEFAULTS.find_my_trip_error_text,
            })}
          </Grid>
        </Grid>
      </Section>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button
          type="button"
          variant="outlined"
          disabled={!isDirty || mutation.isPending}
          onClick={() => reset(defaults)}
        >
          Reset
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!canEdit || !isDirty || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>
    </Box>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{title}</Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </Box>
  );
}
